import type { ApiCtx, Result } from "starfx";
import type { ThunkCtx } from "@app/types";

import { isJSONString, isResult } from "./basic";

import type { TOutput } from "@app/types/helpers";
const isErr = <T>(
  result: Result<T>
): result is { readonly ok: false; error: Error } => {
  return result?.ok === false && "error" in result;
};

const isObjectWithKeys = (
  obj: unknown,
  keys: string[]
): obj is Record<string, unknown> =>
  typeof obj === "object" && obj !== null && keys.every((key) => key in obj);

const isApiCtx = (arg: unknown): arg is ApiCtx =>
  isObjectWithKeys(arg, ["key", "name", "json"]) &&
  (isObjectWithKeys(arg.json, ["ok", "error"]) ||
    isObjectWithKeys(arg.json, ["ok", "value"]));

const isThunkCtx = (arg: unknown): arg is ThunkCtx =>
  isObjectWithKeys(arg, ["action", "actionFn", "key", "name", "result"]) &&
  isObjectWithKeys(arg.result, ["ok"]);

function normalizeError(error) {
  if (error instanceof Error) return error;

  if (typeof error === "object" && error !== null) {
    if ("message" in error) return normalizeError(error.message);
    return new Error(JSON.stringify(error));
  }

  if (typeof error === "string") {
    if (isJSONString(error)) return normalizeError(JSON.parse(error));
    return new Error(error);
  }

  return new Error(JSON.stringify(error));
}

function unpackResult<T>(result: Result<T>): [string | null, T | null] {
  if (result.ok) {
    const value = result.value;

    if (isApiCtx(value)) {
      // console.log('unpackResult isApiCtx', value.json);
      return unpackResult(value.json);
    }

    if (isThunkCtx(value)) {
      // console.log('unpackResult isThunkCtx', value);
      return unpackResult(value.result);
    }

    if (typeof value === "string" && isJSONString(value)) {
      // console.log('unpackResult isJSONString', value);
      const parsedValue = JSON.parse(value);
      if (isResult(parsedValue)) {
        return unpackResult(parsedValue);
      }
    }
    // console.log('unpackResult isOk', value); // debug
    return [null, value];
  } else if (isErr(result)) {
    //that is for the case of an error Result stringified from the endpoint. not an error but a stringified error.
    //{ok: false , error: {message: "some error message"}} | {ok: false , error: "some error message"}
    const extractErrStr = normalizeError(result.error).message;
    return [extractErrStr, null]; // It's an error, return or handle it accordingly.
  }
}

export function matchCall<T>(
  result: Result<T> | unknown,
  customErrorMessage: string | undefined | null,
  output: TOutput[],
  throws = true
): T | Error {
  // console.log('TO EVALUATE:', result);
  // console.log('TYPE OF TO EVALUATE:', typeof result);
  if (result === undefined || result === null) {
    return result as T;
  }
  if (typeof result === "string") {
    if (isJSONString(result)) {
      const parsedResult = JSON.parse(result);
      if (isResult(parsedResult)) {
        return matchCall(
          parsedResult.value,
          customErrorMessage,
          output,
          throws
        );
      } else {
        return matchCall(parsedResult, customErrorMessage, output, throws);
      }
    } else {
      // if(isJSONString(result)) {
      //   const parsedResult = JSON.parse(result);
      //   return matchCall(parsedResult, customErrorMessage, output, throws);
      // } else {
      return result as T;
      // }
    }
  }
  if (isResult(result)) {
    const value = unpackResult(result as Result<T>);
    const [err, val] = value;
    if (err) {
      return matchCall(new Error(err), customErrorMessage, output, throws);
    } else {
      return matchCall(val, customErrorMessage, output, throws);
    }
  } else {
    // any type of something else
    if (result instanceof Error) {
      const newErrMsg1 = result?.message || String(result) || "unknown error";
      const err1 = new Error(newErrMsg1);
      // const msg1 = advertiseError(err1, customErrorMessage, output);
      if (throws) throw new Error(newErrMsg1);
      return new Error(newErrMsg1);
    } else {
      return result as T;
    }
  }
}
/* 

const unpackSafeResult = <T>(result: Result<T>): Result<T> => {
  const verr = unpackResult(result);
  if (verr instanceof Error) {
    return createErr(verr);
  }
  return createOk(verr);
};

const isApiCtx = (arg: unknown): arg is ApiCtx =>
  typeof arg === "object" &&
  arg !== null &&
  "key" in arg &&
  "name" in arg &&
  "json" in arg &&
  typeof (arg as ApiCtx).json === "object" &&
  "ok" in (arg as ApiCtx).json &&
  "data" in (arg as ApiCtx).json;

const isThunkCtx = (arg: unknown): arg is ThunkCtx =>
  typeof arg === "object" &&
  arg !== null &&
  "action" in arg &&
  "actionFn" in arg &&
  "key" in arg &&
  "name" in arg &&
  "result" in arg &&
  typeof (arg as ThunkCtx).result === "object" &&
  "ok" in (arg as ThunkCtx).result;

*/
